import { WorkListTools } from '@/app/work-tabs/work-list-tools';
import { newWorkPath, useWorkTab } from '@/app/work-tabs/work-tab-context';
import { partnerMapFiltersShape } from '@erp/shared';
import { PartnerMapPanel, PartnerMapFilters } from './partner-map-panel';
import {
  PERMISSIONS,
  listViewDefaultColumns,
  customFieldFiltersQuerySchema,
  type ListViewList,
  type CustomFieldFilterValues,
  type CustomFieldPartnerRole,
  type Partner,
  type PartnerType,
} from '@erp/shared';
import { Download, LayoutGrid, List, Plus, Search, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CountrySelect } from '@/components/common/country-select';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/features/auth';
import {
  CustomFieldListFilters,
  activeCustomFieldFilterCount,
  useCustomFieldDefinitions,
} from '@/features/custom-fields';
import {
  usePartnerExport,
  usePartnerList,
  type PartnerListParams,
} from '@/features/partners/api/use-partners';
import { LeadKanbanBoard } from '@/features/partners/components/lead-kanban-board';
import { PartnerFilters } from '@/features/partners/components/partner-filters';
import {
  EMPTY_PARTNER_FILTERS,
  countActiveFilters,
  type PartnerFiltersValue,
} from '@/features/partners/lib/partner-filters-value';
import {
  PartnerFormSheet,
  type PartnerFormPreset,
} from '@/features/partners/components/partner-form-sheet';
import { PartnerTable } from '@/features/partners/components/partner-table';
import { ListViewToolbar, useListView } from '@/features/list-views';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;
const KANBAN_PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 300;

export interface PartnerListPageProps {
  type: PartnerType;
  titleKey: string;
  subtitleKey: string;
  createLabelKey: string;
  formPreset: PartnerFormPreset;
  defaultSort?: string;
  /** Nur die Interessenten-Ansicht bietet zusätzlich das Pipeline-Board. */
  allowKanban?: boolean;
  initialMap?: boolean;
}

export function PartnerListPage({
  type,
  titleKey,
  subtitleKey,
  createLabelKey,
  formPreset,
  defaultSort = 'searchTerm:asc',
  allowKanban = false,
  initialMap = false,
}: PartnerListPageProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const openPartner = (id: string): void => {
    navigate(`/partners/${id}`, {
      state: { partnerListPath: `${location.pathname}${location.search}` },
    });
  };
  const workTab = useWorkTab();
  const { can } = usePermissions();
  const searchRef = useRef<HTMLInputElement>(null);

  const [url] = useSearchParams();
  const definitionRoles: CustomFieldPartnerRole[] =
    type === 'customer'
      ? ['CUSTOMER']
      : type === 'prospect'
        ? ['PROSPECT']
        : type === 'supplier'
          ? ['SUPPLIER']
          : [];
  const definitions = useCustomFieldDefinitions('CUSTOMER', undefined, definitionRoles);
  const listName: ListViewList =
    type === 'all'
      ? 'partners'
      : type === 'customer'
        ? 'customers'
        : type === 'prospect'
          ? 'prospects'
          : 'suppliers';
  const views = useListView(listName, {
    filters: Object.fromEntries(
      [...url].filter(
        ([key]) =>
          key in EMPTY_PARTNER_FILTERS ||
          key in partnerMapFiltersShape ||
          key === 'ownerUserId' ||
          key === 'countryCode' ||
          key === 'customFieldFilters',
      ),
    ),
    sort: url.get('sort') ?? defaultSort,
    columns: [
      ...listViewDefaultColumns(listName),
      ...(definitions.data ?? [])
        .filter((d) => d.showInList && d.archivedAt === null)
        .map((d) => ({ id: `cf-${d.id}`, visible: true })),
    ],
    fieldRefs: {},
    presentation: initialMap ? 'MAP' : allowKanban ? 'BOARD' : 'LIST',
    ...(url.get('q') ? { search: url.get('q') ?? '' } : {}),
  });
  const search = views.state.search ?? '';
  const setSearch = (value: string): void => views.commit({ ...views.state, search: value }, true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const { page, setPage } = views;
  const sort = views.state.sort;
  const setSort = (value: string): void => views.commit({ ...views.state, sort: value });
  const filters = {
    priceGroupIds: (views.state.filters['priceGroupIds'] ?? '').split(',').filter(Boolean),
    subdivisionCode: (views.state.filters['subdivisionCode'] ?? '').split(',').filter(Boolean),
    status: (views.state.filters['status'] ?? '').split(',').filter(Boolean),
    industry: (views.state.filters['industry'] ?? '').split(',').filter(Boolean),
    industryCode: views.state.filters['industryCode'] ?? '',
    tags: (views.state.filters['tags'] ?? '').split(',').filter(Boolean),
    vatIdStatus: (views.state.filters['vatIdStatus'] ?? '').split(',').filter(Boolean),
    riskStatus: (views.state.filters['riskStatus'] ?? '').split(',').filter(Boolean),
    leadStatus: (views.state.filters['leadStatus'] ?? '').split(',').filter(Boolean),
    leadSource: (views.state.filters['leadSource'] ?? '').split(',').filter(Boolean),
    supplierRating: (views.state.filters['supplierRating'] ?? '').split(',').filter(Boolean),
  };
  const setFilters = (next: PartnerFiltersValue): void => {
    const value = { ...views.state.filters };
    for (const [key, entries] of Object.entries(next)) {
      if (entries.length) value[key] = typeof entries === 'string' ? entries : entries.join(',');
      else delete value[key];
    }
    views.commit({ ...views.state, filters: value });
  };
  const [formOpen, setFormOpen] = useState(false);
  const view = (
    views.state.presentation ?? (initialMap ? 'MAP' : allowKanban ? 'BOARD' : 'LIST')
  ).toLowerCase();
  const setView = (value: 'list' | 'board' | 'map') =>
    views.commit({
      ...views.state,
      presentation: value === 'map' ? 'MAP' : value === 'board' ? 'BOARD' : 'LIST',
    });
  const customFieldFilters =
    customFieldFiltersQuerySchema.safeParse(views.state.filters['customFieldFilters']).data ?? {};
  const setCustomFieldFilters = (value: CustomFieldFilterValues): void =>
    views.setFilter('customFieldFilters', JSON.stringify(value));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const isBoard = allowKanban && view === 'board';

  const params: PartnerListParams = {
    page: isBoard ? 1 : page,
    pageSize: isBoard ? KANBAN_PAGE_SIZE : PAGE_SIZE,
    sort,
    type,
    q: debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim(),
    countryCode: views.state.filters['countryCode'],
    priceGroupIds: filters.priceGroupIds,
    subdivisionCode: filters.subdivisionCode,
    status: filters.status,
    industry: filters.industry,
    industryCode: filters.industryCode,
    tags: filters.tags,
    vatIdStatus: filters.vatIdStatus,
    riskStatus: filters.riskStatus,
    leadStatus: filters.leadStatus,
    leadSource: filters.leadSource,
    supplierRating: filters.supplierRating,
    customFieldFilters,
    mapFilters: Object.fromEntries(
      Object.entries(views.state.filters).filter(
        ([key]) => key in partnerMapFiltersShape || key === 'ownerUserId',
      ),
    ),
  };

  const list = usePartnerList(params, views.ready && debouncedSearch === search);
  const exportPartners = usePartnerExport();

  const canWrite = can(PERMISSIONS.CUSTOMER_WRITE);

  const onExport = (format: 'csv' | 'xlsx'): void => {
    exportPartners.mutate(
      { params, format },
      {
        onSuccess: () => toast.success(t('partners.export.success')),
        onError: (error) => toast.error(t('partners.export.error'), { description: error.message }),
      },
    );
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!workTab.active) return;
      const target = event.target as HTMLElement | null;
      const isEditable =
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === '/' && !isEditable) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === 'n' && !isEditable && canWrite) {
        event.preventDefault();
        if (workTab.managed) navigate(newWorkPath(`/partners/new?preset=${formPreset}`));
        else setFormOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canWrite, workTab.active, workTab.managed, navigate, formPreset]);

  const onSaved = (partner: Partner): void => {
    openPartner(partner.id);
  };

  const meta = views.ready ? list.data?.meta : undefined;
  const hasFilters =
    debouncedSearch.trim() !== '' ||
    Boolean(views.state.filters['countryCode']) ||
    countActiveFilters(filters) > 0 ||
    activeCustomFieldFilterCount(customFieldFilters) > 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t(titleKey)}</h1>
          <p className="text-sm text-muted-foreground">{t(subtitleKey)}</p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {
            <div
              role="group"
              aria-label={t('partners.pipeline.viewSwitch')}
              className="flex rounded-md border border-border p-0.5"
            >
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={view === 'list'}
                className={cn(view === 'list' && 'bg-secondary text-secondary-foreground')}
                onClick={() => setView('list')}
              >
                <List className="h-4 w-4" aria-hidden="true" />
                {t('partners.pipeline.listView')}
              </Button>
              {allowKanban ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-pressed={view === 'board'}
                  className={cn(view === 'board' && 'bg-secondary text-secondary-foreground')}
                  onClick={() => setView('board')}
                >
                  <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                  {t('partners.pipeline.boardView')}
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={view === 'map'}
                onClick={() => setView('map')}
              >
                <MapPin className="h-4 w-4" />
                {t('partnerMap.map')}
              </Button>
            </div>
          }

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exportPartners.isPending || !views.ready}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {t('partners.actions.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onExport('csv')}>
                {t('partners.export.csv')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExport('xlsx')}>
                {t('partners.export.xlsx')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {canWrite ? (
            <Button
              size="sm"
              onClick={() =>
                workTab.managed
                  ? navigate(newWorkPath(`/partners/new?preset=${formPreset}`))
                  : setFormOpen(true)
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t(createLabelKey)}
            </Button>
          ) : null}
        </div>
      </header>

      <WorkListTools>
        <ListViewToolbar
          controller={views}
          columnLabel={(id) =>
            definitions.data?.find((d) => `cf-${d.id}` === id || d.key === id)?.label ??
            t(`partners.columns.${id}`, {
              defaultValue: t(`partners.filters.${id}`, { defaultValue: id }),
            })
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-64 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={searchRef}
              className="pl-9"
              placeholder={t('partners.searchPlaceholder')}
              aria-label={t('partners.searchLabel')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <CountrySelect
            value={views.state.filters['countryCode']}
            onChange={(value) => views.setFilter('countryCode', value)}
            clearable
            emptyValueLabel={t('international.allCountries')}
          />
          <PartnerFilters
            type={type}
            value={filters}
            onChange={(next) => {
              setFilters(next);
            }}
            availableTags={list.data?.facets.tags ?? []}
          />
          <CustomFieldListFilters
            definitions={definitions.data ?? []}
            value={customFieldFilters}
            onChange={(next) => {
              setCustomFieldFilters(next);
            }}
          />
        </div>

        <PartnerMapFilters
          type={type}
          value={views.state.filters}
          onChange={(filters) => views.commit({ ...views.state, filters })}
        />
      </WorkListTools>
      {views.error || views.issues.length > 0 ? null : view === 'map' ? (
        <PartnerMapPanel
          params={params}
          ready={views.ready && debouncedSearch === search}
          onPage={setPage}
          onFilters={(filters) =>
            views.commit({ ...views.state, filters: { ...views.state.filters, ...filters } })
          }
        />
      ) : isBoard ? (
        <LeadKanbanBoard
          rows={views.ready && !list.isPlaceholderData ? (list.data?.data ?? []) : []}
          isLoading={list.isPending || !views.ready || list.isPlaceholderData}
          isError={list.isError}
          onRetry={() => void list.refetch()}
          onOpen={openPartner}
          canWrite={canWrite}
        />
      ) : (
        <>
          <PartnerTable
            savedColumns={views.state.columns}
            onSavedColumnsChange={(columns) => views.commit({ ...views.state, columns })}
            type={type}
            canDelete={can(PERMISSIONS.CUSTOMER_DELETE)}
            rows={views.ready && !list.isPlaceholderData ? (list.data?.data ?? []) : []}
            definitions={definitions.data ?? []}
            isLoading={list.isPending || !views.ready || list.isPlaceholderData}
            isError={list.isError}
            onRetry={() => void list.refetch()}
            sort={sort}
            onSortChange={setSort}
            onOpen={openPartner}
            onCreate={
              canWrite
                ? () =>
                    workTab.managed
                      ? navigate(newWorkPath(`/partners/new?preset=${formPreset}`))
                      : setFormOpen(true)
                : undefined
            }
            hasFilters={hasFilters}
          />

          {meta === undefined ? null : (
            <nav
              className="flex items-center justify-between gap-4"
              aria-label={t('common.pagination')}
            >
              <p className="text-xs text-muted-foreground">
                {t('common.paginationInfo', {
                  page: meta.page,
                  totalPages: Math.max(meta.totalPages, 1),
                  total: meta.total,
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {t('common.next')}
                </Button>
              </div>
            </nav>
          )}
        </>
      )}

      <PartnerFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        partner={null}
        preset={formPreset}
        onSaved={onSaved}
      />
    </div>
  );
}
